/**
 * Location Strategy
 *
 * This strategy handles the location topic, including analyzing location requirements
 * and providing recommendations.
 *
 * This implementation uses a closure to track its own state and phase transitions.
 * 
 * Updated to use centralized ICP requirements repository.
 */

import { runAIMatches, runMatchesAnalyze } from '../api'
import generateEnhancedGuidance from './enhancedGuidance'

// Define the strategy phases
const StrategyPhase = {
  INIT: 'INIT_PHASE',
  SUGGESTIONS: 'SUGGESTIONS_PHASE',
  CONVERSATION: 'CONVERSATION_PHASE'
}

/**
 * Extract data from search attributes using a path
 * @param {Object} aiMatchesResult - The AI matches result
 * @param {string} path - The path to extract data from
 * @returns {any} The extracted data
 */
const extractData = (aiMatchesResult, path) => {
  const searchResponse =
    aiMatchesResult?.search_gen_response?.search_attributes || {}

  const responsePath = path
    .split('.')
    .reduce((obj, key) => obj?.[key], searchResponse)

  if (responsePath) {
    return responsePath
  }
}

/**
 * Extract locations from AI matches result
 * @param {Object} aiMatchesResult - The AI matches result
 * @returns {Array} Array of location strings
 */
const extractLocations = (aiMatchesResult) => {
  // First try to get from search_attributes.location_details
  if (
    Array.isArray(
      aiMatchesResult?.search_gen_response?.search_attributes
        ?.location_details
    ) &&
    aiMatchesResult.search_gen_response.search_attributes.location_details
      .length > 0
  ) {
    return aiMatchesResult.search_gen_response.search_attributes.location_details.map(
      loc => loc.formatted_address
    )
  }

  // Fallback to included_location_details if location_details is not available
  if (
    Array.isArray(extractData(aiMatchesResult, 'included_location_details')) &&
    extractData(aiMatchesResult, 'included_location_details').length > 0
  ) {
    return extractData(aiMatchesResult, 'included_location_details').map(
      loc => loc.formatted_address
    )
  }

  // If no locations found in either place, check included_locations
  if (
    Array.isArray(
      aiMatchesResult?.search_gen_response?.search_attributes
        ?.included_locations
    ) &&
    aiMatchesResult.search_gen_response.search_attributes.included_locations
      .length > 0
  ) {
    return aiMatchesResult.search_gen_response.search_attributes
      .included_locations
  }

  return []
}

/**
 * Check if the requirements already have location information
 * @param {Object} aiMatchesResult - The AI matches result
 * @returns {boolean} True if locations are already present
 */
const hasLocationRequirements = (aiMatchesResult) => {
  // Check if there are any location requirements in the ICP requirements
  if (
    aiMatchesResult?.search_gen_response?.icp_requirements &&
    Array.isArray(aiMatchesResult.search_gen_response.icp_requirements)
  ) {
    const locationRequirements = aiMatchesResult.search_gen_response.icp_requirements.filter(
      req => req.section_name.includes('Location')
    )
    return locationRequirements.length > 0
  }
  
  // Also check if there are any locations in the search attributes
  const locations = extractLocations(aiMatchesResult)
  return locations.length > 0
}

/**
 * Handler for the init phase of the location strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Location strategy: init phase')

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing location discovery...`
            }
          : msg
      )
      return updatedMessages
    })
  }

  // Add a "Typing..." message to show the thinking chat
  if (setMessages) {
    // First, remove any existing "Typing..." messages to avoid duplicates
    setMessages(prev => {
      const filteredMessages = prev.filter(
        msg => !(msg.role === 'AI' && msg.content === 'Typing...')
      )

      // Then add a new "Typing..." message with a unique ID and detailed thinking
      const typingMessage = {
        role: 'AI',
        content: 'Typing...',
        thinking:
          'Analyzing location requirements...\nPreparing location discovery phase...\nGathering location data...',
        id: `typing_location_${Date.now()}`, // Add a unique ID
        showThinking: true // Explicitly set showThinking to true
      }

      return [...filteredMessages, typingMessage]
    })
  }

  try {
    // Run AI matches to get requirements
    const aiMatchesResult = await runAIMatches(messages)

    // Update match results in the sidebar if handleRefreshResults is provided
    if (decision && decision.handleRefreshResults) {
      console.log('Updating match results in sidebar with handleRefreshResults')
      decision.handleRefreshResults(messages, aiMatchesResult)
    }

    if (!aiMatchesResult) {
      throw new Error('Failed to get AI matches')
    }

    // Check if locations are already present in the requirements
    const locationsAlreadyPresent = hasLocationRequirements(aiMatchesResult)
    
    // Update thinking accordion with location check
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nChecking if locations are already present: ${locationsAlreadyPresent}`
              }
            : msg
        )
        return updatedMessages
      })
    }

    let locationMap = {}
    let locationKeys = []
    let matchesAnalyzeResult = null

    // Only run matchesAnalyze if locations are not already present
    if (!locationsAlreadyPresent) {
      // Extract requirements from the AI matches result
      const requirements = aiMatchesResult.search_gen_response?.icp_requirements || []

      // Run matches analyze to get location data
      matchesAnalyzeResult = await runMatchesAnalyze(requirements, {
        run_on_self_org: true
      })

      // Check if we have location_raw_map data
      locationMap = matchesAnalyzeResult?.analysis_info?.location_raw_map || {}
      
      // Sort location keys by the number of profiles (array length) in descending order
      locationKeys = Object.keys(locationMap)
        .sort((a, b) => {
          const aProfiles = Array.isArray(locationMap[a]) ? locationMap[a].length : 0;
          const bProfiles = Array.isArray(locationMap[b]) ? locationMap[b].length : 0;
          return bProfiles - aProfiles; // Sort in descending order
        });

      // Update thinking accordion with location data
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking}\nFound ${
                    locationKeys.length
                  } locations in matches analyze: ${locationKeys
                    .slice(0, 5)
                    .join(', ')}${locationKeys.length > 5 ? '...' : ''}`
                }
              : msg
          )
          return updatedMessages
        })
      }
    }

    // Extract locations from the AI matches result
    const locations = extractLocations(aiMatchesResult)
    
    // Generate location guidance based on the data
    let locationGuidance = `Focus on understanding the location requirements for this role. Ask if remote work is an option, if they're looking for candidates in specific regions or time zones, and if relocation assistance is available. Clarify if there are any visa or work authorization requirements.`
    
    if (locationsAlreadyPresent) {
      // If locations are already present, focus on confirming remote/in-office preferences
      locationGuidance += `\n\nThe user has already specified locations: ${locations.join(', ')}. Reiterate the location choice. Focus on confirming if they need candidates to be remotely located, in-office, or if they have other specific location requirements like time zones or visa sponsorship.`
    } else if (locations.length > 0 || locationKeys.length > 0) {
      // If we have locations from either source, suggest them
      const suggestedLocations = locations.length > 0 ? locations : locationKeys.slice(0, 5)
      locationGuidance += `\n\nBased on the search requirements, candidates who match these requirements, the most common locations are: ${suggestedLocations.join(
        ', '
      )} (note: parse this location list and list only cities - remove states and countries). Ask the user if they want to target candidates in these locations or if they have different location preferences.`
    }

    // Remove the "Typing..." message
    // if (setMessages) {
    //   setMessages(prev => {
    //     return prev.filter(
    //       msg => !(msg.role === 'AI' && msg.content === 'Typing...')
    //     )
    //   })
    // }

    // Add an AI message with a specific question about location
    // if (setMessages) {
    //   setMessages(prev => {
    //     let content = `Now, let's talk about location requirements for this role.`
        
    //     if (locationsAlreadyPresent) {
    //       // If locations are already present, focus on remote/in-office preferences
    //       content += ` I see you're interested in locations like ${locations.join(', ')}. Is remote work an option for this role? Do you require candidates to be in-office, or do you have a hybrid approach?`
    //     } else {
    //       // Otherwise, ask about general location preferences
    //       content += ` What are your location preferences? Is remote work an option? Are you looking for candidates in specific regions or time zones?`
          
    //       // Add suggestions if we have them
    //       if (locationKeys.length > 0) {
    //         content += ` Based on your current employees who match these requirements, we see employees in locations like ${locationKeys
    //           .slice(0, 3)
    //           .join(', ')}. Would you like to target candidates in similar areas?`
    //       }
    //     }
        
    //     const locationMessage = {
    //       role: 'AI',
    //       content: 'Lets move on and discover desired locations for this role'
    //     }
    //     return [...prev, locationMessage]
    //   })
    // }
    
    if (locationsAlreadyPresent) {
      locationGuidance += ` I see that the target location for this role is ${locations.join(', ')}. Is remote work an option for this role? Or would you like to add more specific requirements?`
    } else if (locationKeys.length > 0) {
      locationGuidance += ` Based on your current employees who match these requirements, we see employees in locations like ${locationKeys
        .slice(0, 3)
        .join(', ')}. Would you like to target candidates in similar areas?`
    }
    // If we have location keys, return RENDER_LOCATION_KEYS action type
    if (locationKeys.length > 0) {
      return {
        ...decision,
        actionType: 'RENDER_LOCATION_KEYS',
        agentGuidance: locationGuidance,
        locationData: {
          locationMap,
          locationKeys
        },
        onSendText: decision.onSendText,
        advanceToNextTopic: decision.advanceToNextTopic,
        strategyPhase: StrategyPhase.CONVERSATION
      }
    } else {
      // Otherwise, return SEND_WITH_GUIDANCE action type
      return {
        ...decision,
        actionType: 'SEND_WITH_GUIDANCE',
        agentGuidance: locationGuidance,
        aiMatchesResult: {
          ...aiMatchesResult,
          locationData: matchesAnalyzeResult
        },
        strategyPhase: StrategyPhase.CONVERSATION
      }
    }
  } catch (error) {
    console.error('Error in location init phase:', error)

    // Generate default guidance for location topic
    const defaultGuidance = `Focus on understanding the location requirements for this role. Ask if remote work is an option, if they're looking for candidates in specific regions or time zones, and if relocation assistance is available. Clarify if there are any visa or work authorization requirements.`

    // Add a basic AI message about location
    if (setMessages) {
      setMessages(prev => {
        const locationMessage = {
          role: 'AI',
          content: `Now, let's talk about location requirements for this role. What are your location preferences? Is remote work an option? Are you looking for candidates in specific regions or time zones?`
        }
        return [...prev, locationMessage]
      })
    }

    // Update decision to move to the suggestions phase
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: defaultGuidance,
      strategyPhase: StrategyPhase.SUGGESTIONS
    }
  }
}

/**
 * Handler for the suggestions phase of the location strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Location strategy: suggestions phase')

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating location suggestions...`
            }
          : msg
      )
      return updatedMessages
    })
  }

  try {
    // Run AI matches to get location recommendations
    const aiMatchesResult = await runAIMatches(messages)

    if (!aiMatchesResult) {
      throw new Error('Failed to get AI matches')
    }

      // Parse icp_requirements from the AI matches result
      if (
        aiMatchesResult.search_gen_response &&
        aiMatchesResult.search_gen_response.icp_requirements
      ) {
        const icpRequirements =
          aiMatchesResult.search_gen_response.icp_requirements

        // Log the icp_requirements for debugging
        console.log('Location ICP Requirements:', icpRequirements)

        // Check for location requirements
        const locationRequirements = icpRequirements.filter(req =>
          req.section_name.includes('Location')
        )

        if (locationRequirements.length > 0) {
          // Log location requirements
          console.log('Location Requirements:', locationRequirements)

          // Update thinking accordion with location requirements
          if (setMessages) {
            setMessages(prev => {
              const updatedMessages = prev.map(msg =>
                msg.role === 'AI' && msg.content === 'Typing...'
                  ? {
                      ...msg,
                      thinking: `${
                        msg.thinking
                      }\nFound location requirements: ${locationRequirements
                        .map(req => req.bool_str)
                        .join(', ')}`
                    }
                  : msg
              )
              return updatedMessages
            })
          }
          
          // Update centralized ICP requirements if updateRequirements is available
          if (decision.updateRequirements) {
            console.log('Updating centralized ICP requirements with location requirements');
            decision.updateRequirements(locationRequirements, 'location');
          }
        }
      }

    // Generate enhanced agent guidance with Findem Magic explanations
    const enhancedGuidance = await generateEnhancedGuidance(
      aiMatchesResult,
      decision,
      'location'
    )
    
    // Check for Findem Magic requirements in the AI matches result
    if (aiMatchesResult.search_gen_response && aiMatchesResult.search_gen_response.icp_requirements) {
      const findemMagic = aiMatchesResult.search_gen_response.icp_requirements.find(req => 
        req.section_name === "Findem Magic"
      );
      
      if (findemMagic && decision.updateRequirements) {
        console.log('Updating centralized ICP requirements with Findem Magic requirements');
        decision.updateRequirements([findemMagic], 'findem_magic');
      }
    }

    // Update decision with AI matches result and enhanced guidance
    return {
      ...decision,
      actionType: 'SEND_WITH_GUIDANCE',
      agentGuidance: enhancedGuidance,
      aiMatchesResult,
      strategyPhase: StrategyPhase.CONVERSATION
    }
  } catch (error) {
    console.error('Error generating location suggestions:', error)

    // Fall back to conversation phase without suggestions
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    }
  }
}

/**
 * Handler for the conversation phase of the location strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Location strategy: conversation phase')

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about location...`
            }
          : msg
      )
      return updatedMessages
    })
  }

  // Generate specific guidance for the location conversation phase
  const locationConversationGuidance = `
IMPORTANT: The current topic is LOCATION. Focus ONLY on discussing location requirements for this role.

1. First, acknowledge any location information the user has provided and ask for more details:
   - Is remote work an option for this role?
   - Are there specific regions or time zones they're targeting?
   - Is relocation assistance available?
   - Are there any visa or work authorization requirements?

2. Then, explore other aspects of location that might be relevant:
   - If remote, are there any specific requirements (e.g., occasional office visits)?
   - If on-site, is there flexibility for partial remote work?
   - Are there any specific location-based benefits or challenges to consider?

3. After gathering sufficient information, summarize the location requirements clearly:
   - "Based on our conversation, it sounds like you're looking for [summarize location requirements]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that summary of the location requirements look accurate? If so, shall we move on to discussing the job title?"
   - "Have I captured your location needs correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the location discussion.
`

  // In the conversation phase, we pass the user's message to the backend with specific location guidance
  return {
    ...decision,
    actionType: 'SEND_TO_BACKEND',
    agentGuidance: locationConversationGuidance,
    strategyPhase: StrategyPhase.CONVERSATION
  }
}

/**
 * Create a location strategy that tracks its own state
 * This approach uses a closure to maintain state between calls
 */
const createLocationStrategy = () => {
  // Internal state tracking
  let hasExecutedInitPhase = false
  let hasExecutedSuggestionsPhase = false

  // The actual strategy function
  return async (decision, messages, setMessages) => {
    console.log('Location strategy executing with state:', {
      hasExecutedInitPhase,
      hasExecutedSuggestionsPhase,
      incomingPhase: decision.strategyPhase
    })

    // If a specific phase is requested in the decision, honor it
    if (decision.strategyPhase === StrategyPhase.INIT) {
      console.log('Explicitly requested INIT phase')
      hasExecutedInitPhase = true
      return await initPhaseHandler(decision, messages, setMessages)
    } else if (decision.strategyPhase === StrategyPhase.SUGGESTIONS) {
      console.log('Explicitly requested SUGGESTIONS phase')
      hasExecutedSuggestionsPhase = true
      return await suggestionsPhaseHandler(decision, messages, setMessages)
    } else if (decision.strategyPhase === StrategyPhase.CONVERSATION) {
      console.log('Explicitly requested CONVERSATION phase')
      return await conversationPhaseHandler(decision, messages, setMessages)
    }

    // Otherwise, determine which phase to execute based on internal state
    if (!hasExecutedInitPhase) {
      console.log('Executing init phase for first time')
      hasExecutedInitPhase = true
      return await initPhaseHandler(decision, messages, setMessages)
    } else if (!hasExecutedSuggestionsPhase) {
      console.log('Executing suggestions phase for first time')
      hasExecutedSuggestionsPhase = true
      return await suggestionsPhaseHandler(decision, messages, setMessages)
    } else {
      console.log('Executing conversation phase')
      return await conversationPhaseHandler(decision, messages, setMessages)
    }
  }
}

// Export the created strategy
const locationStrategy = createLocationStrategy()
export default locationStrategy
